import { atom } from 'jotai';

/**
 * UX state for the Rainfall analysis layer's controls panel.
 *
 * These atoms are driven by the controls panel and consumed by the
 * VECTOR feature renderer (and the TILE layer's visibility, in the
 * case of `flowAccumulationVisible`). Storing them in Jotai rather
 * than per-layer settings means the controls react immediately without
 * round-tripping through the layer-update mutation.
 *
 * The state is per-tab/session, not persisted — toggling the heatmap
 * off and refreshing brings it back. That matches how planners
 * actually work: explore, then re-frame.
 */

export type RainfallVisibleKind = 'flowLine' | 'pond' | 'watershed';

/**
 * Per-kind visibility for the VECTOR child. Defaults: flow lines + ponds
 * on, watersheds off (the watersheds are revealed per-pond via click —
 * see {@link rainfallSelectedPondIdAtom}).
 */
export const rainfallVisibleKindsAtom = atom<Record<RainfallVisibleKind, boolean>>({
  flowLine: true,
  pond: true,
  watershed: false,
});

/**
 * Lower bound (in m² of upstream contributing area) below which flow
 * lines are hidden client-side. The worker emits every flow line whose
 * accumulation exceeds 50 m² so the slider can move freely above that
 * floor without re-running the job.
 */
export const rainfallAccumulationThresholdAtom = atom<number>(50);

/**
 * If non-null, the controls panel shows the watershed only for this
 * pond (and dims the others). Cleared when the user clicks empty
 * canvas or another feature kind. Independent of
 * {@link rainfallVisibleKindsAtom}.watershed — that toggle hides ALL
 * watersheds; this filter narrows them to one.
 */
export const rainfallSelectedPondIdAtom = atom<string | null>(null);

/**
 * The feature whose properties are shown in the inspector pane. Set on
 * click (any kind), cleared on click-outside. We store the GeoJSON
 * feature directly rather than a layer-id + serial pair because the
 * panel renders kind-specific stats from `properties.*` and never
 * needs to round-trip to the DB.
 */
export interface RainfallSelectedFeature {
  kind: RainfallVisibleKind;
  properties: Record<string, unknown>;
}
export const rainfallSelectedFeatureAtom = atom<RainfallSelectedFeature | null>(null);

/**
 * Visibility toggle for the overflow-chain arrow overlay (the synthetic
 * lines drawn between connected pond centroids — pond → downstream pond).
 * On by default because the chain visualisation is the headline value of
 * STORM mode + multi-pond surveys; users can hide it when ponds are
 * dense enough that arrows clutter the map.
 *
 * The arrows render whenever a rainfall VECTOR layer is selected and at
 * least one pond carries `overflowsIntoPondId`, regardless of mode —
 * overflow chains are computed in TOPOLOGY too.
 */
export const rainfallShowOverflowArrowsAtom = atom<boolean>(true);

/**
 * Per-pond gully state — in-memory **optimistic override** keyed on
 * pondId. The persistent source of truth is `feature.properties.hasGully`
 * stored server-side via `updateFeature`. This atom only carries
 * pending toggles so the UI updates instantly while the mutation is in
 * flight, and continues to override the server value for the rest of
 * the session if the user changes their mind.
 *
 * Read pattern: prefer `isPondDrained(properties, gullies)` from
 * `rainfallStyling.ts` — it implements "atom override wins if key
 * exists, otherwise read `properties.hasGully`" which is the only
 * correct way to combine the two sources.
 *
 * UI semantics: when drained, the pond is treated as drained by a
 * pipe network — it never fills, never overflows downstream, never
 * appears in the time-to-overflow leaderboard, and its capacity flag
 * reads "Drained" instead of computing a captureRatio.
 */
export const rainfallGullyTaggingAtom = atom<Record<string, boolean>>({});

/**
 * Animated storm-playback state (Feature 5). All three live in this
 * file so consumers can subscribe with a single import. Source of
 * truth for the per-frame water-level animation.
 */

/**
 * Current playback time in seconds, 0 = storm start. Consumers compute
 * per-pond water level from this.
 */
export const rainfallPlaybackTimeAtom = atom<number>(0);

/**
 * Whether the playback is actively advancing (RAF loop). The
 * RainfallPlaybackBar component owns the RAF and reads/writes this
 * atom.
 */
export const rainfallPlaybackPlayingAtom = atom<boolean>(false);

/**
 * Speed multiplier (1×, 2×, 5×, 10×). Most storms are minutes-to-hours
 * so realtime is too slow for a demo; default 10×.
 */
export const rainfallPlaybackSpeedAtom = atom<number>(10);

/**
 * What-if storm-intensity multiplier applied client-side to all
 * storm-derived metrics. 1.0 = the storm the user picked at run time.
 * Range [0.5, 3.0]; users typically scrub between "what if half the
 * design" and "what if 3× design".
 *
 * Plain in-memory atom — resets on reload. The slider is a transient
 * exploration tool tied to one viewing session, not a persisted setting:
 * coming back to a project tomorrow should show the design-storm result
 * the worker actually produced, not yesterday's scenario.
 *
 * Every consumer of storm-derived fields multiplies / divides through
 * `app/src/lib/rainfallScaling.ts` — never directly.
 */
export const rainfallStormScaleAtom = atom<number>(1.0);
